import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Plus, Trash2, Share2, X, ArrowUpFromLine, CopyPlus } from 'lucide-react';
import Modal from '../../../components/Modal';
import PriceMatrixEditor from './PriceMatrixEditor';

// ==================== ALIAS MAP (cached) ====================
let _cachedAliasMap = null;
let _aliasMapPromise = null;

function getAliasMap() {
  if (_cachedAliasMap) return _cachedAliasMap;
  if (!_aliasMapPromise) {
    _aliasMapPromise = fetch('/api/pricelists/variable-aliases')
      .then(r => r.ok ? r.json() : { aliasMap: {} })
      .then(d => { _cachedAliasMap = d.aliasMap || {}; return _cachedAliasMap; })
      .catch(() => { _cachedAliasMap = {}; return {}; });
  }
  return {};
}
// Preload on module import
getAliasMap();

function resolveAliasesInFormula(formula) {
  if (!_cachedAliasMap || !formula) return formula;
  return formula.replace(/\b([gle]_[a-z0-9_]+)\b/gi, (_, varName) => {
    const lower = varName.toLowerCase();
    const prefix = lower.substring(0, 2);
    const name = lower.substring(2);
    const canonical = _cachedAliasMap[name] || name;
    return prefix + canonical;
  });
}

// ==================== CELL TYPE ====================

function cellType(raw) {
  if (raw === undefined || raw === null || raw === '') return 'empty';
  if (typeof raw === 'number') return 'number';
  const s = String(raw);
  if (s.startsWith("'")) return 'text';
  if (s.startsWith('=')) return 'formula';
  if (!isNaN(s) && s.trim() !== '') return 'number';
  return 'text';
}

// ==================== BILINEAR INTERPOLATION ====================

function bilinearInterpolate(matrix, rowVal, colVal) {
  const { rowAxis, colAxis, data } = matrix;
  if (!rowAxis?.values?.length || !colAxis?.values?.length || !data?.length) return 0;
  const rv = Math.max(rowAxis.values[0], Math.min(rowVal, rowAxis.values[rowAxis.values.length - 1]));
  const cv = Math.max(colAxis.values[0], Math.min(colVal, colAxis.values[colAxis.values.length - 1]));
  let ri = 0;
  for (let i = 0; i < rowAxis.values.length - 1; i++) { if (rowAxis.values[i + 1] >= rv) { ri = i; break; } ri = i; }
  let ci = 0;
  for (let i = 0; i < colAxis.values.length - 1; i++) { if (colAxis.values[i + 1] >= cv) { ci = i; break; } ci = i; }
  const ri2 = Math.min(ri + 1, rowAxis.values.length - 1), ci2 = Math.min(ci + 1, colAxis.values.length - 1);
  const r1 = rowAxis.values[ri], r2 = rowAxis.values[ri2], c1 = colAxis.values[ci], c2 = colAxis.values[ci2];
  const q11 = data[ri]?.[ci] || 0, q21 = data[ri2]?.[ci] || 0, q12 = data[ri]?.[ci2] || 0, q22 = data[ri2]?.[ci2] || 0;
  if (r2 === r1 && c2 === c1) return q11;
  if (r2 === r1) return q11 + (q12 - q11) * (cv - c1) / (c2 - c1);
  if (c2 === c1) return q11 + (q21 - q11) * (rv - r1) / (r2 - r1);
  const tr = (rv - r1) / (r2 - r1), tc = (cv - c1) / (c2 - c1);
  return q11 * (1 - tr) * (1 - tc) + q21 * tr * (1 - tc) + q12 * (1 - tr) * tc + q22 * tr * tc;
}

// ==================== FORMULA ENGINE ====================

function evaluateFormula(formula, row, allRows, rowIndex, variables, visited, matrices) {
  if (typeof formula !== 'string' || !formula.startsWith('=')) return formula;
  if (!visited) visited = new Set();
  const expr = formula.substring(1);
  try {
    let resolved = expr;

    // SUM(col)
    resolved = resolved.replace(/SUM\(([a-zA-Z])\)/gi, (_, col) => {
      const key = col.toUpperCase();
      const sum = allRows.reduce((acc, r, i) => {
        if (i === rowIndex) return acc;
        return acc + (getNumericValue(r[key], r, allRows, i, variables, new Set(visited), matrices) || 0);
      }, 0);
      return String(sum);
    });

    resolved = resolved.replace(/ROUND\(([^,]+),\s*(\d+)\)/gi, (_, e, d) =>
      `Math.round((${e}) * Math.pow(10, ${d})) / Math.pow(10, ${d})`);
    resolved = resolved.replace(/MIN\(([^)]+)\)/gi, (_, a) => `Math.min(${a})`);
    resolved = resolved.replace(/MAX\(([^)]+)\)/gi, (_, a) => `Math.max(${a})`);
    // Parse IF() with balanced parentheses
    let ifSafe = 0;
    while (/IF\(/i.test(resolved) && ifSafe++ < 20) {
      const prev = resolved;
      const idx = resolved.search(/IF\(/i);
      if (idx < 0) break;
      let depth = 0, start = idx + 3, args = [], argStart = start;
      for (let i = start; i < resolved.length; i++) {
        if (resolved[i] === '(') depth++;
        else if (resolved[i] === ')') { if (depth === 0) { args.push(resolved.substring(argStart, i)); break; } depth--; }
        else if (resolved[i] === ',' && depth === 0) { args.push(resolved.substring(argStart, i)); argStart = i + 1; }
      }
      if (args.length === 3) {
        const endPos = resolved.indexOf(')', start + args.join(',').length);
        const cond = args[0].replace(/([^=!<>])=([^=])/g, '$1==$2');
        const replacement = `((${cond}) ? (${args[1]}) : (${args[2]}))`;
        resolved = resolved.substring(0, idx) + replacement + resolved.substring(endPos + 1);
      }
      if (resolved === prev) break;
    }

    // MATRIX(id, rowVal, colVal)
    if (matrices) {
      resolved = resolved.replace(/MATRIX\(([a-zA-Z0-9_]+),\s*([^,]+),\s*([^)]+)\)/gi, (_, matId, rowExpr, colExpr) => {
        const matrix = matrices[matId];
        if (!matrix) return '0';
        let rVal = resolveAliasesInFormula(rowExpr.trim()), cVal = resolveAliasesInFormula(colExpr.trim());
        if (variables) {
          rVal = rVal.replace(/\b([gle]_[a-z0-9_]+)\b/gi, (_, v) => { const val = variables[v.toLowerCase()]; return val !== undefined ? String(val) : '0'; });
          cVal = cVal.replace(/\b([gle]_[a-z0-9_]+)\b/gi, (_, v) => { const val = variables[v.toLowerCase()]; return val !== undefined ? String(val) : '0'; });
        }
        const colKeys = new Set((row ? Object.keys(row) : []).map(k => k.toUpperCase()));
        rVal = rVal.replace(/(?<![a-zA-Z_0-9])([a-zA-Z])(?![a-zA-Z_0-9])/g, (m, c) => colKeys.has(c.toUpperCase()) ? String(getNumericValue(row[c.toUpperCase()], row, allRows, rowIndex, variables, new Set(visited), matrices)) : m);
        cVal = cVal.replace(/(?<![a-zA-Z_0-9])([a-zA-Z])(?![a-zA-Z_0-9])/g, (m, c) => colKeys.has(c.toUpperCase()) ? String(getNumericValue(row[c.toUpperCase()], row, allRows, rowIndex, variables, new Set(visited), matrices)) : m);
        try {
          // eslint-disable-next-line no-eval
          const rv = eval(rVal), cv = eval(cVal);
          if (typeof rv === 'number' && typeof cv === 'number') return String(Math.round(bilinearInterpolate(matrix, rv, cv) * 100) / 100);
        } catch {}
        return '0';
      });
    }

    // Resolve aliases (e.g. l_discount → l_rabat) BEFORE variable lookup
    resolved = resolveAliasesInFormula(resolved);

    // Replace variables (g_, l_, e_) BEFORE column letters
    if (variables) {
      resolved = resolved.replace(/\b([gle]_[a-z0-9_]+)\b/gi, (_, varName) => {
        const v = variables[varName.toLowerCase()];
        return v !== undefined ? String(v) : '0';
      });
    }

    // Column letters (A-Z, case insensitive)
    // Only match standalone letters that are actual column keys, not part of other tokens
    const colKeys = new Set((row ? Object.keys(row) : []).map(k => k.toUpperCase()));
    resolved = resolved.replace(/(?<![a-zA-Z_0-9])([a-zA-Z])(?![a-zA-Z_0-9])/g, (match, col) => {
      const key = col.toUpperCase();
      if (!colKeys.has(key)) return match; // not a known column — leave as is
      const cellKey = `${rowIndex}:${key}`;
      if (visited.has(cellKey)) return '0';
      visited.add(cellKey);
      return String(getNumericValue(row[key], row, allRows, rowIndex, variables, visited, matrices));
    });

    if (/^[\d\s+\-*/().,%<>=?:!&|]+$/.test(resolved) || /Math\./.test(resolved)) {
      // eslint-disable-next-line no-eval
      const result = eval(resolved);
      if (typeof result === 'number' && isFinite(result)) return Math.round(result * 100) / 100;
    }
    return '#ERR';
  } catch { return '#ERR'; }
}

function getNumericValue(cellData, row, allRows, rowIndex, variables, visited, matrices) {
  if (cellData === undefined || cellData === null || cellData === '') return 0;
  if (typeof cellData === 'number') return cellData;
  const s = String(cellData);
  if (s.startsWith("'")) return 0;
  if (s.startsWith('=')) return evaluateFormula(s, row, allRows, rowIndex, variables, visited, matrices);
  const num = parseFloat(s.replace(/\s/g,'').replace(',','.'));
  return isNaN(num) ? 0 : num;
}

function getDisplayCellValue(cellData, row, allRows, rowIndex, variables, matrices) {
  const t = cellType(cellData);
  if (t === 'empty') return '';
  if (t === 'text') return String(cellData).startsWith("'") ? String(cellData).substring(1) : cellData;
  if (t === 'number') return cellData;
  if (t === 'formula') return evaluateFormula(cellData, row, allRows, rowIndex, variables, undefined, matrices);
  return cellData;
}

// ==================== DEFAULT ====================

const DEFAULT_COLUMNS = [
  { key: 'A', label: 'Opis', width: 180 },
  { key: 'B', label: 'Ilość', width: 80 },
  { key: 'C', label: 'Jedn.', width: 60 },
  { key: 'D', label: 'Cena/jedn.', width: 100 },
  { key: 'E', label: 'Wartość', width: 100 },
];
const DEFAULT_ROW = { A: "'", B: '', C: "'szt", D: '', E: '=B*D' };

function getEmptySpreadsheet() {
  return { columns: DEFAULT_COLUMNS.map(c => ({ ...c })), rows: [{ ...DEFAULT_ROW }], exports: {} };
}

// ==================== AUTOCOMPLETE ====================

function FormulaAutocomplete({ suggestions, filter, onSelect, position }) {
  const filtered = suggestions.filter(s =>
    s.key.toLowerCase().includes(filter.toLowerCase()) ||
    (s.description && s.description.toLowerCase().includes(filter.toLowerCase()))
  );
  if (filtered.length === 0) return null;

  const typeColor = (key) => {
    if (key.startsWith('g_')) return 'text-blue-600';
    if (key.startsWith('l_')) return 'text-orange-600';
    if (key.startsWith('e_')) return 'text-teal-600';
    return 'text-gray-600';
  };

  return (
    <div className="fixed z-[100] bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto text-xs"
      style={{ top: position.top, left: position.left, minWidth: 260 }}>
      {filtered.map(s => (
        <button key={s.key} onClick={() => onSelect(s.key)}
          className="w-full text-left px-3 py-1.5 hover:bg-emerald-50 flex items-center gap-2 border-b border-gray-50 last:border-0">
          <span className={`font-mono font-medium ${typeColor(s.key)}`}>{s.key}</span>
          <span className="text-gray-400 truncate flex-1">{s.description || ''}</span>
          {s.value !== undefined && <span className="text-gray-500 flex-shrink-0">= {s.value}</span>}
        </button>
      ))}
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

const COLUMN_PRESETS = [
  'Opis', 'Nazwa', 'Szerokość', 'Wysokość', 'Długość', 'Głębokość',
  'Objętość', 'Powierzchnia', 'Waga', 'J.m.', 'Ilość',
  'Cena/j.m.', 'Wartość', 'Rabat', 'Marża', 'Narzut',
  'Materiał', 'Uwagi', 'SKU', 'Kod',
];

export default function MiniSpreadsheet({ data, onChange, readOnly, variables, parentExports, showTotal = true }) {
  const [spreadsheet, setSpreadsheet] = useState(() => {
    if (data && data.columns && data.columns.length > 0) return { exports: {}, ...data };
    return getEmptySpreadsheet();
  });
  const [editingHeader, setEditingHeader] = useState(null);
  const [focusedCell, setFocusedCell] = useState(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteFilter, setAutocompleteFilter] = useState('');
  const [autocompletePos, setAutocompletePos] = useState({ top: 0, left: 0 });
  const [exportModal, setExportModal] = useState(null);
  const [isEditingFormula, setIsEditingFormula] = useState(false); // true when formula bar has =...
  const saveTimer = useRef(null);
  const cellRefs = useRef({});

  // Colors for cell references in formulas
  const REF_COLORS = ['#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED', '#EC4899', '#0891B2', '#4F46E5'];

  // Parse formula to find cell references (e.g. B, D, E in =B*D+E)
  const parseFormulaRefs = (formula) => {
    if (!formula || !formula.startsWith('=')) return [];
    const refs = [];
    const expr = formula.substring(1);
    // Remove variables first
    const cleaned = expr.replace(/[gle]_[a-z0-9_]+/gi, '');
    // Find single column letters
    const matches = cleaned.matchAll(/(?<![a-zA-Z_0-9])([a-zA-Z])(?![a-zA-Z_0-9])/g);
    const seen = new Set();
    for (const m of matches) {
      const key = m[1].toUpperCase();
      if (!seen.has(key) && spreadsheet.columns.some(c => c.key === key)) {
        refs.push(key);
        seen.add(key);
      }
    }
    return refs;
  };

  // Build variables map: g_ + l_ + e_ (parent exports)
  const variablesMap = {};
  if (variables) {
    (variables.global || []).forEach(v => { variablesMap[v.key] = parseFloat(v.value) || 0; });
    (variables.local || []).forEach(v => { variablesMap[v.key] = parseFloat(v.value) || 0; });
  }
  if (parentExports) {
    parentExports.forEach(pe => { variablesMap[`e_${pe.label}`] = parseFloat(pe.value) || 0; });
  }

  // Suggestions for autocomplete (with aliases)
  const suggestions = [];
  const lang = localStorage.getItem('swibi_language') || 'pl';
  const addWithAlias = (key, value, description) => {
    suggestions.push({ key, value, description });
    // Add alias in current language if different
    if (_cachedAliasMap) {
      const prefix = key.substring(0, 2); // "g_", "l_", "e_"
      const name = key.substring(2);
      // Reverse lookup: find alias for current language
      // _cachedAliasMap maps alias→canonical, we need canonical→alias for lang
      const allAliases = Object.entries(_cachedAliasMap);
      for (const [alias, canonical] of allAliases) {
        if (canonical === name && alias !== name) {
          suggestions.push({ key: prefix + alias, value, description: `= ${key} ${description || ''}`.trim(), isAlias: true });
        }
      }
    }
  };
  if (variables) {
    (variables.global || []).forEach(v => addWithAlias(v.key, v.value, v.description));
    (variables.local || []).forEach(v => addWithAlias(v.key, v.value, v.description));
  }
  if (parentExports) {
    parentExports.forEach(pe => addWithAlias(
      `e_${pe.label}`, pe.value, `${pe.source} → ${pe.description || pe.label}`
    ));
  }

  const computeResult = useCallback((ss) => {
    if (!ss.columns.length) return 0;
    const lastCol = ss.columns[ss.columns.length - 1];
    const total = ss.rows.reduce((sum, row, i) => {
      const val = getNumericValue(row[lastCol.key], row, ss.rows, i, variablesMap, undefined, ss.matrices || {});
      return sum + (isFinite(val) ? val : 0);
    }, 0);
    return isFinite(total) ? Math.round(total * 100) / 100 : 0;
  // eslint-disable-next-line
  }, [JSON.stringify(variablesMap)]);

  // Save to API — only on blur or structural changes (add/remove row/col, exports)
  const dirty = useRef(false);
  const latestSS = useRef(spreadsheet);

  const saveNow = useCallback((ss) => {
    dirty.current = false;
    latestSS.current = ss;
    onChange(ss, computeResult(ss));
  }, [onChange, computeResult]);

  const scheduleSave = useCallback((ss) => {
    dirty.current = true;
    latestSS.current = ss;
    // No timer — save happens on blur
  }, []);

  // Flush pending save on blur
  const flushSave = useCallback(() => {
    if (dirty.current) {
      saveNow(latestSS.current);
    }
  }, [saveNow]);

  // Update local state only (no API call)
  const updateLocal = useCallback((newSS) => {
    setSpreadsheet(newSS);
    scheduleSave(newSS);
  }, [scheduleSave]);

  // Update and save immediately (structural changes)
  const updateAndSave = useCallback((newSS) => {
    setSpreadsheet(newSS);
    saveNow(newSS);
  }, [saveNow]);

  const setCellValue = (rowIdx, colKey, value) => {
    const newRows = spreadsheet.rows.map((r, i) => i === rowIdx ? { ...r, [colKey]: value } : r);
    const isFormula = typeof value === 'string' && value.startsWith('=');
    setIsEditingFormula(isFormula);
    if (isFormula) {
      const match = value.match(/([gle]_[a-z0-9_]*)$/i);
      if (match) {
        setAutocompleteFilter(match[1]);
        const ref = cellRefs.current[`${rowIdx}:${colKey}`];
        if (ref) {
          const rect = ref.getBoundingClientRect();
          setAutocompletePos({ top: rect.bottom + 2, left: rect.left });
        }
        setShowAutocomplete(true);
      } else { setShowAutocomplete(false); }
    } else { setShowAutocomplete(false); }
    updateLocal({ ...spreadsheet, rows: newRows });
  };

  const handleAutocompleteSelect = (varKey) => {
    if (!focusedCell) return;
    const { row, col } = focusedCell;
    const currentVal = String(spreadsheet.rows[row]?.[col] || '');
    const newVal = currentVal.replace(/([gle]_[a-z0-9_]*)$/i, varKey);
    setCellValue(row, col, newVal);
    setShowAutocomplete(false);
    // Refocus
    const ref = cellRefs.current[`${row}:${col}`];
    if (ref) setTimeout(() => ref.focus(), 50);
  };

  const addRow = () => {
    const lastRow = spreadsheet.rows[spreadsheet.rows.length - 1];
    const newRow = {};
    spreadsheet.columns.forEach(col => {
      const lastVal = lastRow ? lastRow[col.key] : '';
      newRow[col.key] = (typeof lastVal === 'string' && lastVal.startsWith('=')) ? lastVal : '';
    });
    updateAndSave({ ...spreadsheet, rows: [...spreadsheet.rows, newRow] });
  };

  const duplicateRow = (idx, direction) => {
    const source = spreadsheet.rows[idx];
    if (!source) return;
    const copy = { ...source };
    const newRows = [...spreadsheet.rows];
    const insertAt = direction === 'above' ? idx : idx + 1;
    newRows.splice(insertAt, 0, copy);
    updateAndSave({ ...spreadsheet, rows: newRows });
  };

  const removeRow = (idx) => {
    if (spreadsheet.rows.length <= 1) return;
    const newExports = { ...spreadsheet.exports };
    Object.keys(newExports).forEach(k => { if (k.startsWith(`${idx}:`)) delete newExports[k]; });
    updateAndSave({ ...spreadsheet, rows: spreadsheet.rows.filter((_, i) => i !== idx), exports: newExports });
  };

  const addColumn = () => {
    const usedKeys = spreadsheet.columns.map(c => c.key);
    const nextKey = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').find(k => !usedKeys.includes(k));
    if (!nextKey) return;
    updateAndSave({ ...spreadsheet, columns: [...spreadsheet.columns, { key: nextKey, label: nextKey, width: 100 }] });
  };

  const removeColumn = (key) => {
    if (spreadsheet.columns.length <= 1) return;
    const newCols = spreadsheet.columns.filter(c => c.key !== key);
    const newRows = spreadsheet.rows.map(r => { const nr = { ...r }; delete nr[key]; return nr; });
    const newExports = { ...spreadsheet.exports };
    Object.keys(newExports).forEach(k => { if (k.endsWith(`:${key}`)) delete newExports[k]; });
    updateAndSave({ ...spreadsheet, columns: newCols, rows: newRows, exports: newExports });
  };

  const updateColumnLabel = (key, label) => {
    updateAndSave({ ...spreadsheet, columns: spreadsheet.columns.map(c => c.key === key ? { ...c, label } : c) });
  };

  // Export
  const saveExport = (rowIdx, colKey, label, description) => {
    const ref = `${rowIdx}:${colKey}`;
    updateAndSave({ ...spreadsheet, exports: { ...spreadsheet.exports, [ref]: { label: label.toLowerCase().replace(/[^a-z0-9_]/g, '_'), description } } });
  };

  const removeExport = (rowIdx, colKey) => {
    const ref = `${rowIdx}:${colKey}`;
    const newExports = { ...spreadsheet.exports };
    delete newExports[ref];
    updateAndSave({ ...spreadsheet, exports: newExports });
  };

  const resultValue = computeResult(spreadsheet);
  const exports = spreadsheet.exports || {};

  useEffect(() => {
    if (!focusedCell) { const t = setTimeout(() => setShowAutocomplete(false), 200); return () => clearTimeout(t); }
  }, [focusedCell]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-visible bg-white">

      {/* Parent exports info bar */}
      {parentExports && parentExports.length > 0 && (
        <div className="px-3 py-1.5 bg-teal-50 border-b border-teal-200 flex items-center gap-2 flex-wrap">
          <ArrowUpFromLine size={14} className="text-teal-600 flex-shrink-0" />
          <span className="text-[11px] text-teal-700 font-medium flex-shrink-0">Z gałęzi nadrzędnych:</span>
          {parentExports.map(pe => (
            <span key={pe.label} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-800 rounded text-[11px] font-mono">
              e_{pe.label}
              <span className="text-teal-500">= {Number(pe.value).toLocaleString('pl-PL', { maximumFractionDigits: 2 })}</span>
              {pe.description && <span className="text-teal-400 font-sans ml-1">({pe.description})</span>}
            </span>
          ))}
        </div>
      )}

      {/* Formula bar */}
      {!readOnly && focusedCell && (
        <div className="px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-400 bg-white border rounded px-1.5 py-0.5 w-8 text-center flex-shrink-0">
            {spreadsheet.columns.find(c => c.key === focusedCell.col)?.key || '?'}{focusedCell.row + 1}
          </span>
          {/* Reference color legend */}
          {(() => {
            const raw = String(spreadsheet.rows[focusedCell.row]?.[focusedCell.col] ?? '');
            const refs = parseFormulaRefs(raw);
            if (refs.length === 0) return null;
            return (
              <div className="flex items-center gap-1 flex-shrink-0">
                {refs.map((r, i) => (
                  <span key={r} className="text-[9px] font-mono font-bold px-1 rounded" style={{ color: REF_COLORS[i % REF_COLORS.length], backgroundColor: REF_COLORS[i % REF_COLORS.length] + '15' }}>
                    {r}
                  </span>
                ))}
              </div>
            );
          })()}
          <input
            ref={el => { cellRefs.current['formulaBar'] = el; }}
            data-cell="true"
            type="text"
            value={String(spreadsheet.rows[focusedCell.row]?.[focusedCell.col] ?? '')}
            onChange={e => setCellValue(focusedCell.row, focusedCell.col, e.target.value)}
            onBlur={e => {
              const related = e.relatedTarget;
              if (related && related.dataset?.cell) return;
              setIsEditingFormula(false); setFocusedCell(null); flushSave();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setIsEditingFormula(false);
                flushSave();
                const nextRow = focusedCell.row + 1;
                if (nextRow < spreadsheet.rows.length) {
                  setFocusedCell({ row: nextRow, col: focusedCell.col });
                } else {
                  setFocusedCell(null); e.target.blur();
                }
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                setIsEditingFormula(false);
                flushSave();
                const colIdx = spreadsheet.columns.findIndex(c => c.key === focusedCell.col);
                const nextCol = spreadsheet.columns[colIdx + (e.shiftKey ? -1 : 1)];
                if (nextCol) setFocusedCell({ row: focusedCell.row, col: nextCol.key });
              }
              if (e.key === 'Escape') { setIsEditingFormula(false); setShowAutocomplete(false); setFocusedCell(null); e.target.blur(); }
              if (e.key === 'ArrowDown') { e.preventDefault(); const nr = focusedCell.row + 1; if (nr < spreadsheet.rows.length) setFocusedCell({ row: nr, col: focusedCell.col }); }
              if (e.key === 'ArrowUp') { e.preventDefault(); const nr = focusedCell.row - 1; if (nr >= 0) setFocusedCell({ row: nr, col: focusedCell.col }); }
            }}
            className="flex-1 px-2 py-0.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-emerald-400 focus:border-transparent font-mono"
            placeholder="Wpisz wartosc lub formule (=A*B)"
          />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="w-8 px-1 py-1.5 border-b border-r border-gray-200 text-center text-xs text-gray-400">#</th>
              {spreadsheet.columns.map(col => (
                <th key={col.key} className="border-b border-r border-gray-200 px-1 py-1 group/col" style={{ minWidth: col.width }}>
                  <div className="flex items-center gap-1">
                    {editingHeader === col.key ? (
                      <div className="relative flex-1">
                        <input type="text" defaultValue={col.label} list={`presets-${col.key}`}
                          className="w-full px-1 py-0.5 text-xs border border-emerald-400 rounded focus:outline-none" autoFocus
                          onBlur={e => { updateColumnLabel(col.key, e.target.value); setEditingHeader(null); }}
                          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
                        <datalist id={`presets-${col.key}`}>
                          {COLUMN_PRESETS.map(p => <option key={p} value={p} />)}
                        </datalist>
                      </div>
                    ) : (
                      <span className="flex-1 text-xs font-medium text-gray-600 cursor-pointer truncate"
                        onDoubleClick={() => !readOnly && setEditingHeader(col.key)} title="2x klik = wybierz nagłówek">
                        {col.label}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{col.key}</span>
                    {!readOnly && spreadsheet.columns.length > 1 && (
                      <button onClick={() => removeColumn(col.key)}
                        className="opacity-0 group-hover/col:opacity-100 hover:text-red-500 text-gray-300 flex-shrink-0">
                        <Trash2 size={10} />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              {!readOnly && (
                <th className="w-16 border-b border-gray-200 px-1 py-1">
                  <button onClick={addColumn} className="text-xs text-gray-400 hover:text-emerald-600" title="Dodaj kolumnę"><Plus size={14} /></button>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {spreadsheet.rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-gray-50/50 group/row">
                <td className="px-1 py-0.5 border-r border-b border-gray-100 text-center text-xs text-gray-300">{rowIdx + 1}</td>
                {spreadsheet.columns.map(col => {
                  const raw = row[col.key] ?? '';
                  const ct = cellType(raw);
                  const displayed = getDisplayCellValue(raw, row, spreadsheet.rows, rowIdx, variablesMap, spreadsheet.matrices || {});
                  const isFocused = focusedCell?.row === rowIdx && focusedCell?.col === col.key;
                  const isErr = displayed === '#ERR';
                  const isNumeric = ct === 'number' || ct === 'formula';
                  const align = isNumeric ? 'text-right' : 'text-left';
                  const exportRef = `${rowIdx}:${col.key}`;
                  const isExported = !!exports[exportRef];

                  let colorClass = '';
                  if (!isFocused && ct === 'formula') colorClass = isErr ? 'text-red-500' : 'text-purple-700';

                  return (
                    <td key={col.key}
                      className={`border-r border-b border-gray-100 p-0 relative ${isExported ? 'bg-amber-50' : ''}`}
                      style={{ minWidth: col.width }}>
                      {isExported && (
                        <div className="absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-amber-400 border-l-[8px] border-l-transparent cursor-pointer"
                          title={`Eksport: e_${exports[exportRef].label}`}
                          onClick={() => !readOnly && setExportModal({ row: rowIdx, col: col.key, current: exports[exportRef] })} />
                      )}
                      {readOnly ? (
                        <div className={`px-2 py-1 text-sm ${align} ${colorClass}`}>
                          {typeof displayed === 'number' ? displayed.toLocaleString('pl-PL', { maximumFractionDigits: 2 }) : displayed}
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <div
                            ref={el => { cellRefs.current[`${rowIdx}:${col.key}`] = el; }}
                            tabIndex={0}
                            data-cell="true"
                            onClick={() => {
                              if (isEditingFormula && focusedCell && !(focusedCell.row === rowIdx && focusedCell.col === col.key)) {
                                // Insert cell reference into formula
                                const currentVal = String(spreadsheet.rows[focusedCell.row]?.[focusedCell.col] ?? '');
                                const ref = col.key;
                                setCellValue(focusedCell.row, focusedCell.col, currentVal + ref);
                                // Keep focus on formula bar
                                setTimeout(() => { const fb = cellRefs.current['formulaBar']; if (fb) { fb.focus(); if (!fb.value.startsWith('=')) fb.select(); } }, 0);
                                return;
                              }
                              const cellRaw = String(spreadsheet.rows[rowIdx]?.[col.key] ?? '');
                              setIsEditingFormula(cellRaw.startsWith('='));
                              setFocusedCell({ row: rowIdx, col: col.key });
                              setTimeout(() => { const fb = cellRefs.current['formulaBar']; if (fb) { fb.focus(); if (!fb.value.startsWith('=')) fb.select(); } }, 0);
                            }}
                            onFocus={() => {
                              if (isEditingFormula && focusedCell) return; // Don't steal focus during formula edit
                              setFocusedCell({ row: rowIdx, col: col.key });
                              setTimeout(() => { const fb = cellRefs.current['formulaBar']; if (fb) { fb.focus(); if (!fb.value.startsWith('=')) fb.select(); } }, 0);
                            }}
                            className={`w-full px-2 py-1 text-sm cursor-cell select-none ${align} ${colorClass} ${isFocused ? 'ring-2 ring-inset ring-emerald-400 bg-emerald-50' : ''} ${
                              // Highlight referenced cells
                              (() => {
                                if (!focusedCell) return '';
                                const focusedRaw = spreadsheet.rows[focusedCell.row]?.[focusedCell.col] ?? '';
                                const refs = parseFormulaRefs(String(focusedRaw));
                                const refIdx = refs.indexOf(col.key);
                                if (refIdx >= 0 && focusedCell.row === rowIdx) {
                                  return `ring-2 ring-inset`;
                                }
                                return '';
                              })()
                            }`}
                            style={(() => {
                              if (!focusedCell) return {};
                              const focusedRaw = spreadsheet.rows[focusedCell.row]?.[focusedCell.col] ?? '';
                              const refs = parseFormulaRefs(String(focusedRaw));
                              const refIdx = refs.indexOf(col.key);
                              if (refIdx >= 0 && focusedCell.row === rowIdx) {
                                return { boxShadow: `inset 0 0 0 2px ${REF_COLORS[refIdx % REF_COLORS.length]}` };
                              }
                              return {};
                            })()}
                          >
                            {typeof displayed === 'number' ? displayed.toLocaleString('pl-PL', { maximumFractionDigits: 2 }) : String(displayed)}
                          </div>
                          {isFocused && (
                            <button
                              onMouseDown={e => { e.preventDefault(); setExportModal({ row: rowIdx, col: col.key, current: exports[exportRef] || null }); }}
                              className={`flex-shrink-0 p-0.5 mr-0.5 rounded ${isExported ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'}`}
                              title="Udostępnij komórkę gałęziom podrzędnym">
                              <Share2 size={11} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
                {!readOnly && (
                  <td className="border-b border-gray-100 px-1 py-0.5">
                    <div className="flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 transition">
                      <button onClick={() => duplicateRow(rowIdx, 'above')}
                        className="p-0.5 hover:bg-blue-50 rounded text-gray-300 hover:text-blue-500" title="Kopiuj wiersz wyzej">
                        <CopyPlus size={11} className="rotate-180" />
                      </button>
                      <button onClick={() => duplicateRow(rowIdx, 'below')}
                        className="p-0.5 hover:bg-blue-50 rounded text-gray-300 hover:text-blue-500" title="Kopiuj wiersz nizej">
                        <CopyPlus size={11} />
                      </button>
                      <button onClick={() => removeRow(rowIdx)}
                        className="p-0.5 hover:bg-red-50 rounded text-gray-300 hover:text-red-500" title="Usun wiersz">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Autocomplete */}
      {showAutocomplete && suggestions.length > 0 && (
        <FormulaAutocomplete suggestions={suggestions} filter={autocompleteFilter}
          onSelect={handleAutocompleteSelect} position={autocompletePos} />
      )}

      {/* Price Matrix Editor */}
      {(spreadsheet.matrices && Object.keys(spreadsheet.matrices).length > 0) || !readOnly ? (
        <PriceMatrixEditor
          matrices={spreadsheet.matrices || {}}
          onChange={(newMatrices) => {
            const hasMatrices = Object.keys(newMatrices).length > 0;
            const updated = { ...spreadsheet, matrices: hasMatrices ? newMatrices : undefined };
            if (!hasMatrices) delete updated.matrices;
            updateAndSave(updated);
          }}
          readOnly={readOnly}
        />
      ) : null}

      {/* Footer */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-4">
          {!readOnly && (
            <button onClick={addRow} className="flex items-center gap-1 text-xs text-gray-500 hover:text-emerald-600 transition">
              <Plus size={14} /> Wiersz
            </button>
          )}
          <span className="text-[11px] text-gray-400">
            <span className="text-purple-600">=</span>formuła &middot;
            123 &middot;
            <span className="text-blue-600">'</span>tekst &middot;
            <span className="text-blue-600">g_</span> <span className="text-orange-600">l_</span> <span className="text-teal-600">e_</span> zmienne
          </span>
        </div>
        {showTotal && (
          <div className="text-sm font-medium text-gray-800">
            Razem: <span className="text-emerald-700">{resultValue.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={!!exportModal}
        cellRef={exportModal ? `${exportModal.row + 1}:${spreadsheet.columns.find(c => c.key === exportModal?.col)?.label || exportModal?.col}` : ''}
        cellValue={exportModal ? (() => {
          const row = spreadsheet.rows[exportModal.row];
          const raw = row?.[exportModal.col];
          if (!raw || raw === '') return '(pusta komórka!)';
          const ct = cellType(raw);
          if (ct === 'formula') {
            const displayed = getDisplayCellValue(raw, row, spreadsheet.rows, exportModal.row, variablesMap, spreadsheet.matrices || {});
            return typeof displayed === 'number' ? displayed : String(displayed);
          }
          if (ct === 'text' && String(raw).startsWith("'")) return String(raw).substring(1);
          return raw;
        })() : ''}
        current={exportModal?.current}
        onSave={(label, desc) => { saveExport(exportModal.row, exportModal.col, label, desc); setExportModal(null); }}
        onRemove={() => { removeExport(exportModal.row, exportModal.col); setExportModal(null); }}
        onClose={() => setExportModal(null)}
      />
    </div>
  );
}

// ==================== EXPORT MODAL ====================

function ExportModal({ isOpen, cellRef, cellValue, current, onSave, onRemove, onClose }) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLabel(current?.label || '');
      setDescription(current?.description || '');
    }
  }, [isOpen, current]);

  const isEmpty = cellValue === '(pusta komórka!)' || cellValue === '' || cellValue === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Udostępnij komórkę ${cellRef}`}>
      <div className="space-y-3">
        {/* Current value preview */}
        <div className={`p-3 rounded-lg border ${isEmpty ? 'bg-red-50 border-red-200' : 'bg-teal-50 border-teal-200'}`}>
          <div className="text-xs text-gray-500 mb-1">Aktualna wartość komórki:</div>
          <div className={`text-lg font-bold ${isEmpty ? 'text-red-500' : 'text-teal-700'}`}>
            {isEmpty ? 'PUSTA — wybierz komórkę z wartością!' : cellValue}
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Wartość tej komórki będzie dostępna w gałęziach podrzędnych jako zmienna <span className="font-mono text-teal-600">e_<span className="text-teal-800">{label || '...'}</span></span>
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Etykieta (nazwa zmiennej) *</label>
          <div className="flex items-center gap-1">
            <span className="text-sm font-mono text-teal-600">e_</span>
            <input type="text" value={label} onChange={e => setLabel(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="np. koszt_materialow" autoFocus />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Opis</label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            placeholder="np. Całkowity koszt materiałów budowlanych" />
        </div>
        <div className="flex items-center justify-between pt-2">
          {current ? (
            <button onClick={onRemove} className="text-sm text-red-600 hover:underline">Usuń udostępnienie</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
            <button onClick={() => onSave(label, description)} disabled={!label.trim()}
              className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg">
              {current ? 'Zapisz' : 'Udostępnij'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { getEmptySpreadsheet };
